import { useGeckoNotifications } from 'features/notifications'
import { useRolodexHistogram } from 'features/rolodex'
import findIndex from 'lodash/findIndex'
import reduce from 'lodash/reduce'
import uniq from 'lodash/uniq'
import { useMemo } from 'react'
import {
  RolodexEntryType,
  RolodexHistogramFilter,
  RolodexHistogramQuery,
} from 'types'

import { useOntology } from './OntologyProvider'
import { OntologyField, OntologyRolodexEntryType } from './ontologyTypes'

export const RD_QUERY_LIMIT = 60000
const FALLBACK_QUERY_TYPE: OntologyRolodexEntryType = 'organization'

type UseInflectedQueryProps = {
  select: RolodexHistogramQuery['select']
  filters: readonly RolodexHistogramFilter[]
  queryType?: OntologyRolodexEntryType
}

type UseInflectedQueryResult = {
  filters: readonly RolodexHistogramFilter[]
  tags: RolodexHistogramQuery['tags'] | null
  type: OntologyRolodexEntryType
}

function useInflectedQueries(
  props: UseInflectedQueryProps | ReadonlyArray<UseInflectedQueryProps>
): {
  queryResults: ReadonlyArray<UseInflectedQueryResult>
  isLoading: boolean
} {
  const propsArray = useMemo<ReadonlyArray<UseInflectedQueryProps>>(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  const { errorNotification } = useGeckoNotifications()
  const { bucketLinkedTypes, getInflectionType, findLowestType } = useOntology()

  const bucketCalculations = useMemo(() => {
    return propsArray.map(({ select, filters, queryType }) => {
      const selectTypes = select
        .map((s) => s.type as OntologyRolodexEntryType)
        .filter((item): item is NonNullable<typeof item> => !!item)
      const filterTypes = filters
        .map((f) => f.type as OntologyRolodexEntryType)
        .filter((item): item is NonNullable<typeof item> => !!item)
      const allTypes = uniq(
        [...selectTypes, ...filterTypes, queryType].filter(
          (item): item is NonNullable<typeof item> => !!item
        )
      )
      const bucketedTypes = bucketLinkedTypes(allTypes)

      if (bucketedTypes.length <= 1) {
        return { selectBucket: allTypes, reduceBuckets: [] }
      }

      const selectBucketIdx = findIndex(bucketedTypes, (bucket) =>
        selectTypes.every((sT) => bucket.includes(sT))
      )

      if (selectBucketIdx < 0) {
        errorNotification(
          "Something went wrong - couldn't find a single bucket with the select terms"
        )
        return { selectBucket: allTypes, reduceBuckets: [] }
      }

      const reduceBuckets = bucketedTypes.filter(
        (_, idx) => idx !== selectBucketIdx
      )

      return { selectBucket: bucketedTypes[selectBucketIdx], reduceBuckets }
    })
  }, [propsArray, bucketLinkedTypes, errorNotification])

  const bucketQueries = useMemo(() => {
    return bucketCalculations.flatMap(
      ({ selectBucket, reduceBuckets }, idx) => {
        const { filters } = propsArray[idx]

        return reduceBuckets
          .map((reduceBucket) => {
            const inflectionType = getInflectionType(selectBucket, reduceBucket)
            if (!inflectionType) {
              errorNotification(`No inflection type for ${inflectionType}`)
              return null
            }

            const filterRelevantTypes = [...reduceBucket, inflectionType]
            const lowestType = findLowestType(filterRelevantTypes)

            return lowestType
              ? {
                  select: [
                    {
                      type: inflectionType as RolodexEntryType,
                      field: 'id',
                      group_by: true,
                      label: 'inflection_id',
                    },
                  ],
                  filters: filters.filter((filter) =>
                    filterRelevantTypes.includes(
                      filter.type as OntologyRolodexEntryType
                    )
                  ),
                  links: uniq(
                    filterRelevantTypes.filter((t) => t !== lowestType)
                  ) as RolodexEntryType[],
                  type: lowestType as RolodexEntryType,
                  tags: {},
                  limit: RD_QUERY_LIMIT,
                }
              : null
          })
          .filter((item): item is NonNullable<typeof item> => !!item)
      }
    )
  }, [
    bucketCalculations,
    propsArray,
    getInflectionType,
    findLowestType,
    errorNotification,
  ])

  const { data: bucketResults, isLoading } = useRolodexHistogram(bucketQueries)

  const queryResults = useMemo(() => {
    let bucketQueryCursor = 0

    return propsArray.map(({ filters, queryType }, idx) => {
      const { selectBucket, reduceBuckets } = bucketCalculations[idx]

      const currentBucketQueries = bucketQueries.slice(
        bucketQueryCursor,
        bucketQueryCursor + reduceBuckets.length
      )
      const currentBucketResults = bucketResults?.slice(
        bucketQueryCursor,
        bucketQueryCursor + reduceBuckets.length
      )
      bucketQueryCursor += reduceBuckets.length

      const inflectionTags: RolodexHistogramQuery['tags'] | null =
        currentBucketQueries.length && currentBucketResults
          ? (() => {
              const bucketInflectionIdSets = currentBucketResults.map(
                (result) =>
                  new Set(
                    result
                      .map((row) => row['inflection_id'] as string | null)
                      .filter((item): item is string => !!item)
                  )
              )

              const bucketInflectionTypes = currentBucketQueries.map(
                (query) => query.select[0].type! as OntologyRolodexEntryType
              )

              const tagSets = reduce(
                bucketInflectionTypes,
                (accum, currType, index) => {
                  return {
                    ...accum,
                    [currType]: accum[currType].intersection(
                      bucketInflectionIdSets[index]
                    ),
                  }
                },
                Object.fromEntries(
                  bucketInflectionTypes.map((t, idx) => [
                    t,
                    new Set(bucketInflectionIdSets[idx] ?? []),
                  ])
                )
              )

              return Object.fromEntries(
                Object.entries(tagSets).map(([type, tagSet]) => [
                  type,
                  [...tagSet],
                ])
              )
            })()
          : null

      return {
        filters: filters.filter(
          (f) =>
            !!f.type &&
            selectBucket.includes(f.type as OntologyRolodexEntryType)
        ),
        tags: inflectionTags
          ? Object.fromEntries(
              Object.entries(inflectionTags).map(([type, values]) => [
                type,
                values.length ? values : ['no-op'],
              ])
            )
          : null,
        type: findLowestType(selectBucket) ?? queryType ?? FALLBACK_QUERY_TYPE,
      }
    })
  }, [
    propsArray,
    bucketCalculations,
    bucketQueries,
    bucketResults,
    findLowestType,
  ])

  return useMemo(() => ({ queryResults, isLoading }), [queryResults, isLoading])
}

export type UseHistogramQueryProps = {
  select: RolodexHistogramQuery['select']
  filters: readonly RolodexHistogramFilter[]
  queryType?: OntologyRolodexEntryType
  limit?: number
}

export function useHistogramQueries(
  props: UseHistogramQueryProps | ReadonlyArray<UseHistogramQueryProps>
): {
  isLoading: boolean
  queries: ReadonlyArray<RolodexHistogramQuery>
} {
  const propsArray: ReadonlyArray<UseHistogramQueryProps> = useMemo(
    () => (Array.isArray(props) ? props : [props]),
    [props]
  )

  const { isLoading, queryResults } = useInflectedQueries(
    propsArray.map(({ select, filters, queryType }) => ({
      select,
      filters,
      queryType,
    }))
  )

  const queries: ReadonlyArray<RolodexHistogramQuery> = useMemo(() => {
    return queryResults.map(({ filters, tags, type }, index) => {
      const { select } = propsArray[index]

      const filterTypes = filters.map((f) => f.type ?? FALLBACK_QUERY_TYPE)
      const selectTypes = select.map((s) => s.type ?? FALLBACK_QUERY_TYPE)
      const links = uniq(
        [...filterTypes, ...selectTypes].filter((t) => t !== type)
      ) as RolodexEntryType[]

      return {
        type: type as RolodexEntryType,
        links,
        filters: [...filters],
        select,
        tags: tags ?? {},
        limit: propsArray[index].limit ?? RD_QUERY_LIMIT,
      }
    })
  }, [queryResults, propsArray])

  return useMemo(
    () => ({
      queries,
      isLoading,
    }),
    [queries, isLoading]
  )
}

export function useFieldValues(
  field: OntologyField,
  options?: {
    filters?: readonly RolodexHistogramFilter[]
    limit?: number
  }
): {
  isLoading: boolean
  values: ReadonlyArray<string>
} {
  const { fields } = useOntology()

  const { key, entryType: type, keyOrField } = fields?.[field] ?? {}
  const filters = options?.filters ?? []
  const select = useMemo<RolodexHistogramQuery['select']>(
    () => [
      {
        type,
        [keyOrField]: key,
        label: 'value',
        group_by: true,
      },
    ],
    [key, keyOrField, type]
  )

  const {
    queries: [query],
    isLoading: isHistoQueryLoading,
  } = useHistogramQueries({
    select,
    filters,
    limit: options?.limit,
    queryType: type,
  })
  const { data, isLoading } = useRolodexHistogram(query)

  return useMemo(
    () => ({
      values: data?.flat().map((e) => e['value'] as string) ?? [],
      isLoading: isLoading || isHistoQueryLoading,
    }),
    [data, isHistoQueryLoading, isLoading]
  )
}
